import { useCallback, useId, useState } from 'react';
import ProgressiveDisclosure from '../../shared/ProgressiveDisclosure';
import useDisclosureCardClick from '../../shared/useDisclosureCardClick';
import { Pill, Photo } from '../ClientHybridCard';
import { toneTextStyle } from '../clientsConstants';
import { formatPhone } from '../../../lib/phone';
import { entriesOf, methodLabelText, primaryEntry } from '../../../lib/contactMethods';
import {
    ACCESS_LABELS, addPersonalToCompany, deletePersonalContact, savePersonalContact, sharedByName,
} from '../../../lib/personalContacts';
import { supabaseClient } from '../../../config/supabase';
import PersonalContactForm, { draftFromPersonal } from './PersonalContactForm';
import PersonalSharePanel from './PersonalSharePanel';
import '../../../styles/step2jCrm.css';

import { BTN, TILE, TILE_INTERACTIVE } from '../../ui/platformStandard';
// BL-177 PR D8: the same List / Card / Hybrid views and click-to-reveal grammar as company contacts (a row or tile opens the peek card in
// place; click again for the full card; double-click collapses) over a My Contact.
//
// BL-177 Step 2j: the same cards as the company Clients view - the fixed peacock strip on every Card and Hybrid card, and Hybrid is two
// cards per row, each two halves. A My Contact is not linked to any trip, quote or conversation, so its right half cannot be "Current
// activity" (it would always read "No trips yet / No open items"); it carries the contact's Reach instead: every phone, email and address.

const COLUMNS = ['Name', 'Company', 'Position', 'Phone', 'Email', 'Access'];

const primaryPhone = (contact) => primaryEntry(entriesOf(contact, 'phone'))?.value || '';
const primaryEmail = (contact) => primaryEntry(entriesOf(contact, 'email'))?.value || '';

/** "Mine" / "Shared by Alice · View only" - the small chips every My Contact carries. */
function Chips({ contact, isOwner, colleagues, accessLabel }) {
    return (
        <div className="flex flex-wrap items-center gap-1.5">
            {isOwner ? <Pill tone="muted" title="Only you can see this unless you share it">Private</Pill>
                : <Pill tone="system" title="A colleague shared this with you">Shared by {sharedByName(contact, colleagues)}</Pill>}
            {!isOwner && accessLabel && <Pill tone="muted">{accessLabel}</Pill>}
            {contact.added_to_company_contact_id && <Pill tone="good" title="A copy is in Company contacts">In company contacts</Pill>}
        </div>
    );
}

function EntryList({ contact, kind, format = v => v }) {
    const rows = entriesOf(contact, kind).filter(e => (e.value || '').trim());
    if (!rows.length) return <span className="text-[var(--ink-muted)]">—</span>;
    return (
        <ul className="space-y-0.5">
            {rows.map(entry => (
                <li key={entry.id || entry.value} className="text-sm text-[var(--ink)]">
                    <span className="mr-1.5 text-xs text-[var(--ink-muted)]">{methodLabelText(entry.label)}</span>
                    {format(entry.value)}
                    {entry.is_primary && rows.length > 1 && <span className="ml-1.5 text-[11px] font-semibold uppercase text-[var(--ink-muted)]">Primary</span>}
                </li>
            ))}
        </ul>
    );
}

function Peek({ contact, isOwner, colleagues, accessLabel, onShowMore, onCollapseAll, showIdentity, showStrip = false, twoHalves = false }) {
    const contentId = useId();
    const { onClick, onDoubleClick, onKeyDown, onPointerDownCapture } = useDisclosureCardClick({ onOpen: onShowMore, onClose: onCollapseAll });
    return (
        <div role="group" tabIndex={0}
            className={`${showStrip ? `${TILE} ia-tile3d--clip pt-[5px]` : 'rounded-xl border border-[var(--surface-border)] bg-[var(--surface-raised)]'} ${twoHalves ? 'ia-hybrid-card flex flex-col' : ''}`}
            data-testid={twoHalves ? 'personal-hybrid-card' : undefined}
            onClick={onClick} onDoubleClick={onDoubleClick} onKeyDown={onKeyDown} onPointerDownCapture={onPointerDownCapture}>
            <div className={twoHalves ? 'ia-hybrid-halves flex-1' : undefined}>
                <div className="min-w-0 p-4">
                    <button type="button" aria-expanded={false} aria-controls={contentId} onClick={onShowMore} className="mb-2 text-left font-semibold text-[var(--ink)]">
                        {contact.name}
                    </button>
                    <div id={contentId} className="flex gap-4">
                        {showIdentity && <Photo contact={contact} size="lg" />}
                        <div className="min-w-0 flex-1 space-y-2">
                            <div className="text-sm text-[var(--ink-muted)]">
                                {[contact.position, contact.company_name].filter(Boolean).join(' · ') || '—'}
                            </div>
                            <Chips contact={contact} isOwner={isOwner} colleagues={colleagues} accessLabel={accessLabel} />
                            {!twoHalves && (
                                <div className="grid gap-3 sm:grid-cols-2">
                                    <div><div className="text-[11px] uppercase tracking-wide text-[var(--ink-muted)]">Phone</div><EntryList contact={contact} kind="phone" format={formatPhone} /></div>
                                    <div><div className="text-[11px] uppercase tracking-wide text-[var(--ink-muted)]">Email</div><EntryList contact={contact} kind="email" /></div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                {twoHalves && (
                    <section aria-label="Reach" data-testid="personal-reach" className="ia-hybrid-peek min-w-0 space-y-3 bg-[var(--surface-sunken)] p-4">
                        <div className="text-[11px] uppercase tracking-wide text-[var(--ink-muted)]">Reach</div>
                        <div><div className="text-[11px] uppercase tracking-wide text-[var(--ink-muted)]">Phone</div><EntryList contact={contact} kind="phone" format={formatPhone} /></div>
                        <div><div className="text-[11px] uppercase tracking-wide text-[var(--ink-muted)]">Email</div><EntryList contact={contact} kind="email" /></div>
                        <div><div className="text-[11px] uppercase tracking-wide text-[var(--ink-muted)]">Address</div><EntryList contact={contact} kind="address" /></div>
                    </section>
                )}
            </div>
        </div>
    );
}

function AddToCompany({ contact, entities, onDone }) {
    const [entityId, setEntityId] = useState(entities.length === 1 ? entities[0].id : '');
    const [busy, setBusy] = useState(false);
    const [duplicates, setDuplicates] = useState(null);
    const [message, setMessage] = useState(null);
    const [error, setError] = useState(null);

    const run = async (allowDuplicate) => {
        setBusy(true); setError(null); setMessage(null);
        try {
            const outcome = await addPersonalToCompany(supabaseClient, { contactId: contact.id, entityId, allowDuplicate });
            if (outcome.duplicates) { setDuplicates(outcome.duplicates); return; }
            setDuplicates(null);
            setMessage('Added to Company contacts. The original stays in My Contacts.');
            onDone?.();
        } catch (err) { setError(err.message || String(err)); } finally { setBusy(false); }
    };

    return (
        <div className="mt-3 rounded-lg border border-[var(--surface-border)] bg-[var(--surface-sunken)] p-3" data-testid="add-to-company">
            <div className="flex flex-wrap items-center gap-2">
                {entities.length > 1 && (
                    <select aria-label="Company to add to" value={entityId} onChange={event => setEntityId(event.target.value)}
                        className="rounded border border-[var(--surface-border)] bg-[var(--surface-sunken)] px-2 py-1.5 text-sm text-[var(--ink)]">
                        <option value="">Choose a company…</option>
                        {entities.map(e => <option key={e.id} value={e.id}>{e.entity_code} — {e.name}</option>)}
                    </select>
                )}
                <button type="button" disabled={!entityId || busy} onClick={() => run(false)}
                    className={BTN}>
                    Add to company contacts
                </button>
                <span className="text-xs text-[var(--ink-muted)]">Everyone with access to the company will see the copy. The original stays in My Contacts.</span>
            </div>
            {duplicates && (
                <div role="alert" className="mt-2 rounded border border-[var(--surface-border)] bg-[var(--surface-raised)] p-3 text-sm text-[var(--ink)]">
                    <div className="font-semibold">Someone with this phone or email is already a company contact</div>
                    <ul className="mt-1 list-disc pl-5 text-[var(--ink-muted)]">
                        {duplicates.map(d => <li key={d.contact_id}>{d.name} — same {d.matched_on}</li>)}
                    </ul>
                    <div className="mt-2 flex gap-2">
                        <button type="button" disabled={busy} onClick={() => run(true)} className={BTN}>Add anyway</button>
                        <button type="button" onClick={() => setDuplicates(null)} className="rounded px-3 py-1 text-xs font-medium text-[var(--ink-muted)] hover:text-[var(--ink)]">Cancel</button>
                    </div>
                </div>
            )}
            {message && <div role="status" className="mt-2 text-sm" style={toneTextStyle('good')}>{message}</div>}
            {error && <div role="alert" className="mt-2 text-sm" style={toneTextStyle('alert')}>{error}</div>}
        </div>
    );
}

function Full({ contact, isOwner, canEdit, colleagues, accessLabel, entities, sharingEnabled, userId, editor, onCollapse, onChanged }) {
    const [showShare, setShowShare] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [error, setError] = useState(null);
    const remove = async () => {
        setDeleting(true); setError(null);
        try { await deletePersonalContact(supabaseClient, contact.id); await onChanged?.(); } catch (err) { setError(err.message || String(err)); setDeleting(false); }
    };
    return (
        <div className="overflow-hidden rounded-xl border border-[var(--surface-border)] bg-[var(--surface-raised)]" data-testid="personal-full-card">
            <div className="peacock-band h-1.5 w-full" data-testid="peacock-strip" aria-hidden="true" />
            <div className="p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                        <button type="button" aria-expanded onClick={onCollapse} className="text-left text-lg font-semibold text-[var(--ink)]">{contact.name}</button>
                        <div className="text-sm text-[var(--ink-muted)]">{[contact.position, contact.company_name].filter(Boolean).join(' · ') || '—'}</div>
                        <div className="mt-1"><Chips contact={contact} isOwner={isOwner} colleagues={colleagues} accessLabel={accessLabel} /></div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {canEdit && !editor.editing && (
                            <button type="button" onClick={editor.startEditing} className={BTN}>Edit</button>
                        )}
                        {isOwner && sharingEnabled && (
                            <button type="button" aria-pressed={showShare} onClick={() => setShowShare(v => !v)} className={BTN}>Share…</button>
                        )}
                    </div>
                </div>

                {editor.editing ? (
                    <div className="mt-3">
                        <PersonalContactForm draft={editor.draft} onChange={draft => Object.entries(draft).forEach(([k, v]) => { if (draft[k] !== editor.draft[k]) editor.updateDraft(k, v); })} idPrefix={`edit-${contact.id}`} />
                        {editor.error && <div role="alert" className="mt-2 text-sm" style={toneTextStyle('alert')}>{editor.error}</div>}
                        <div className="mt-3 flex gap-2">
                            <button type="button" disabled={editor.saving || !editor.draft.name.trim()} onClick={editor.save}
                                className={BTN}>{editor.saving ? 'Saving…' : 'Save'}</button>
                            <button type="button" onClick={editor.cancelEditing} className={BTN}>Cancel</button>
                        </div>
                    </div>
                ) : (
                    <div className="mt-3 grid gap-4 sm:grid-cols-2">
                        <div><div className="text-[11px] uppercase tracking-wide text-[var(--ink-muted)]">Phone</div><EntryList contact={contact} kind="phone" format={formatPhone} /></div>
                        <div><div className="text-[11px] uppercase tracking-wide text-[var(--ink-muted)]">Email</div><EntryList contact={contact} kind="email" /></div>
                        <div className="sm:col-span-2"><div className="text-[11px] uppercase tracking-wide text-[var(--ink-muted)]">Address</div><EntryList contact={contact} kind="address" /></div>
                        {contact.notes && <div className="sm:col-span-2 text-sm text-[var(--ink)]"><div className="text-[11px] uppercase tracking-wide text-[var(--ink-muted)]">Notes</div>{contact.notes}</div>}
                    </div>
                )}

                {isOwner && showShare && <PersonalSharePanel userId={userId} contact={contact} onChanged={onChanged} />}
                {entities.length > 0 && <AddToCompany contact={contact} entities={entities} onDone={onChanged} />}   {/* the owner and anyone it is shared with (View or Edit) */}
                {isOwner && (
                    <div className="mt-3 flex justify-end">
                        <button type="button" disabled={deleting} onClick={remove} className="text-xs text-[var(--ink-muted)] underline hover:text-[var(--ink)]">Delete this contact</button>
                    </div>
                )}
                {error && <div role="alert" className="mt-2 text-sm" style={toneTextStyle('alert')}>{error}</div>}
            </div>
        </div>
    );
}

function Row({ contact, isOpen, onToggle, detail, isOwner, colleagues, accessLabel }) {
    return (
        <>
            <tr onClick={() => onToggle(contact.id)} aria-expanded={isOpen}
                className={`cursor-pointer transition hover:bg-[var(--surface-sunken)] ${isOpen ? 'bg-[var(--surface-sunken)]' : ''}`}>
                <td className="px-4 py-3"><div className="flex items-center gap-2.5 min-w-0"><Photo contact={contact} size="sm" /><span className="truncate font-medium text-[var(--ink)]">{contact.name}</span></div></td>
                <td className="px-4 py-3 truncate text-sm text-[var(--ink)]">{contact.company_name || '—'}</td>
                <td className="px-4 py-3 truncate text-sm" style={toneTextStyle('system')}>{contact.position || '—'}</td>
                <td className="px-4 py-3 text-sm text-[var(--ink)]">{primaryPhone(contact) ? formatPhone(primaryPhone(contact)) : '—'}</td>
                <td className="px-4 py-3 truncate text-sm text-[var(--ink)]">{primaryEmail(contact) || '—'}</td>
                <td className="px-4 py-3"><Chips contact={contact} isOwner={isOwner} colleagues={colleagues} accessLabel={accessLabel} /></td>
            </tr>
            {isOpen && <tr className="bg-[var(--surface-canvas)]"><td colSpan={COLUMNS.length} className="px-4 pb-4 pt-1">{detail}</td></tr>}
        </>
    );
}

// Card view (Val, Oct 25 2026): a personal contact is a business card too - same tile and layout as company Clients.
function Tile({ contact, onOpen, isOwner, colleagues, accessLabel }) {
    const phone = primaryPhone(contact);
    const email = primaryEmail(contact);
    const address = primaryEntry(entriesOf(contact, 'address'))?.value || '';
    return (
        <button type="button" aria-expanded={false} onClick={() => onOpen(contact.id)}
            className={`${TILE_INTERACTIVE} ia-bizcard`} data-testid="personal-business-card">
            <div className="ia-bizcard__top">
                <div className="ia-bizcard__company">
                    <Photo contact={contact} size="sm" />
                    <span className="ia-bizcard__company-name">{contact.company_name || 'My contact'}</span>
                </div>
                <Chips contact={contact} isOwner={isOwner} colleagues={colleagues} accessLabel={accessLabel} />
            </div>
            <div className="min-w-0">
                <div className="ia-bizcard__name">{contact.name}</div>
                {contact.position && <div className="ia-bizcard__title">{contact.position}</div>}
            </div>
            <div className="flow-rule ia-bizcard__rule" aria-hidden="true" />
            <div className="ia-bizcard__bottom">
                <div className="ia-bizcard__lines">
                    <div className="ia-bizcard__line"><span>{phone ? formatPhone(phone) : <span className="text-[var(--ink-muted)]">No phone</span>}</span></div>
                    <div className="ia-bizcard__line"><span>{email || <span className="text-[var(--ink-muted)]">No email</span>}</span></div>
                    {address && <div className="ia-bizcard__line"><span title={address}>{address}</span></div>}
                </div>
            </div>
        </button>
    );
}

export { COLUMNS as PERSONAL_COLUMNS };

export default function PersonalContactDisclosure({ contact, mode, userId, colleagues, access = 'owner', entities, sharingEnabled, onChanged }) {
    const isList = mode === 'List';
    const initialTier = mode === 'Hybrid' ? 2 : 1;
    const isOwner = contact.owner_id === userId;
    const canEdit = isOwner || access === 'edit';      // a colleague edits only with a Can-edit share (the database enforces it too)
    const accessLabel = isOwner ? '' : ACCESS_LABELS[canEdit ? 'edit' : 'view'];
    const save = useCallback(async (draft) => {
        await savePersonalContact(supabaseClient, { contact, draft });
        await onChanged?.();
    }, [contact, onChanged]);
    const shared = { isOwner, colleagues, accessLabel };

    return (
        <ProgressiveDisclosure
            record={contact}
            initialTier={initialTier}
            createDraft={draftFromPersonal}
            onSave={save}
            renderTier1={({ showPeek, showFull }) => (isList
                ? <Row contact={contact} isOpen={false} onToggle={showPeek} {...shared} />
                : <Tile contact={contact} onOpen={mode === 'Card' ? showFull : showPeek} {...shared} />)}
            renderTier2={({ showFull, collapse }) => {
                const card = <Peek contact={contact} onShowMore={showFull} onCollapseAll={collapse} showIdentity={!isList} showStrip={!isList} twoHalves={mode === 'Hybrid'} {...shared} />;
                if (!isList) return mode === 'Card' ? <div className="sm:col-span-2 xl:col-span-3">{card}</div> : card;
                return <Row contact={contact} isOpen onToggle={collapse} detail={card} {...shared} />;
            }}
            renderTier3={(editor) => {
                const card = (
                    <Full contact={contact} canEdit={canEdit} entities={entities} sharingEnabled={sharingEnabled} userId={userId} editor={editor}
                        onCollapse={editor.showPeek} onChanged={onChanged} {...shared} />
                );
                if (!isList) return <div className={mode === 'Card' ? 'sm:col-span-2 xl:col-span-3' : 'sm:col-span-2'}>{card}</div>;
                return <Row contact={contact} isOpen onToggle={editor.collapse} detail={card} {...shared} />;
            }}
        />
    );
}
